import React, { useEffect, useState } from "react";

// material-ui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  CircularProgress,
  useTheme,
} from "@mui/material";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

// project imports
import CircularWithPath from "../../../../../../components/@extended/progress/CircularWithPath";
import { openSnackbar } from "../../../../../../api/snackbar";

// assets
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// types
import { SnackbarProps } from "../../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import ReusableModal from "../../../../../../components/ReusableComponent/ReusableModal";
import {
  VacancyPackage,
  VacancyPackageFormData,
} from "../../../../../../types/orders-managment/packages";
import { packageTypeOptions } from "./mockData";
import { theme } from "antd";

// constant
const getInitialValues = (
  vacancyPackage: VacancyPackage | null
): VacancyPackageFormData => {
  if (vacancyPackage) {
    return {
      type: vacancyPackage.type || "",
      count: vacancyPackage.count || 0,
      fees: vacancyPackage.fees || 0,
      price_per_unit: vacancyPackage.price_per_unit || 0,
      package_validity: vacancyPackage.package_validity || 0,
      package_duration: vacancyPackage.package_duration || 0,
      note: vacancyPackage.note || "",
      note_trans: vacancyPackage.note_trans?.ar || "",
      order: vacancyPackage.order || 0,
    };
  } else {
    return {
      type: "",
      count: 0,
      fees: 0,
      price_per_unit: 0,
      package_validity: 0,
      package_duration: 0,
      note: "",
      note_trans: "",
      order: 0,
    };
  }
};

// ==============================|| VACANCY PACKAGE ADD / EDIT - FORM ||============================== //

const AddEditVacancyPackage = ({
  open,
  vacancyPackage,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
}: {
  open: boolean;
  loading: boolean;
  id: string;
  setLoading: any;
  fetchData: any;
  vacancyPackage: VacancyPackage | null;
  handleClose: () => void;
  setResult: any;
}) => {
  const intl = useIntl();
  const theme = useTheme();

  useEffect(() => {
    setLoading(false);
  }, []);

  const VacancyPackageSchema = Yup.object().shape({
    type: Yup.string().required("Type is required"),
    count: Yup.number().required("Count is required"),
    fees: Yup.number().required("Fees is required"),
    price_per_unit: Yup.number().required("Price Per Unit is required"),
    package_validity: Yup.number().required("Validity Per Unit is required"),
    package_duration: Yup.number().required("Duration Per Unit is required"),
    order: Yup.number().required("Order Per Unit is required"),
    note: Yup.string().required("English note is required"),
    note_trans: Yup.string().required("Arabic note is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(vacancyPackage!),
    validationSchema: VacancyPackageSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const vacancyPackageData = {
          type: values.type,
          count: values.count,
          fees: values.fees,
          price_per_unit: values.price_per_unit,
          package_validity: values.package_validity,
          package_duration: values.package_duration,
          note: values.note,
          note_trans: { ar: values.note_trans },
          order: values.order,
        };

        if (vacancyPackage) {
          const response = await USERAXIOS.post(
            `/vacancy_packages/${vacancyPackage?.id}`,
            vacancyPackageData
          );
          const updatedVacancyPackage = response.data.data;

          if (updatedVacancyPackage) {
            setResult((prev) =>
              prev.map((pkg) =>
                pkg.id === updatedVacancyPackage.id
                  ? updatedVacancyPackage
                  : pkg
              )
            );

            openSnackbar({
              open: true,
              message: "Vacancy Package updated successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(
            `/vacancy_packages`,
            vacancyPackageData
          );

          const newVacancyPackage = response.data.data;

          if (newVacancyPackage) {
            setResult((prev) => [...prev, newVacancyPackage]);

            openSnackbar({
              open: true,
              message: "Vacancy Package added successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );

  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle
            color="primary"
            style={{
              textAlign: "center",
              fontSize: "22px",
              fontWeight: "bold",
              marginBottom: "10px",
            }}
          >
            {vacancyPackage ? (
              <FormattedMessage id="Edit vacancy package" />
            ) : (
              <FormattedMessage id="Add vacancy package" />
            )}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container>
              <Grid item xs={12} sm={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-type">
                    <FormattedMessage id="Package Type" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title={"Set the type of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <Select
                    id="package-type"
                    displayEmpty
                    {...getFieldProps("type")}
                    onChange={(event: SelectChangeEvent<string>) => {
                      setFieldValue("type", event.target.value);
                    }}
                    error={Boolean(touched.type && errors.type)}
                  >
                    {packageTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {theme.direction === "ltr"
                          ? option.label
                          : option.arabic_label}{" "}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.type && errors.type && (
                    <FormHelperText error>{errors.type}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={5.5} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-count">
                    <FormattedMessage id="Vacancies Count" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title={"Set the count of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="package-count"
                    placeholder="Enter Count"
                    type="number"
                    {...getFieldProps("count")}
                    error={Boolean(touched.count && errors.count)}
                    helperText={touched.count && errors.count}
                  />
                </Stack>
              </Grid>

              <Grid item md={1} />

              <Grid item xs={12} md={5.5} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-fees">
                    <FormattedMessage id="Fees" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title={"Set the fees of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="package-fees"
                    placeholder="Enter Fees"
                    type="number"
                    {...getFieldProps("fees")}
                    error={Boolean(touched.fees && errors.fees)}
                    helperText={touched.fees && errors.fees}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={5.5} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-price-per-unit">
                    <FormattedMessage id="Price Per Unit" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title={"Set the price of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="package-price-per-unit"
                    placeholder="Enter Price Per Unit"
                    type="number"
                    {...getFieldProps("price_per_unit")}
                    error={Boolean(
                      touched.price_per_unit && errors.price_per_unit
                    )}
                    helperText={touched.price_per_unit && errors.price_per_unit}
                  />
                </Stack>
              </Grid>

              <Grid item md={1} />

              <Grid item xs={12} md={5.5} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-validity">
                    <FormattedMessage id="Package Validity" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title={"Set the validity of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="package-validity"
                    placeholder="Enter Validity"
                    type="number"
                    {...getFieldProps("package_validity")}
                    error={Boolean(
                      touched.package_validity && errors.package_validity
                    )}
                    helperText={
                      touched.package_validity && errors.package_validity
                    }
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={5.5} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-duration">
                    <FormattedMessage id="Post Duration" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title={"Set the duration of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="package-duration"
                    placeholder="Enter Duration"
                    type="number"
                    {...getFieldProps("package_duration")}
                    error={Boolean(
                      touched.package_duration && errors.package_duration
                    )}
                    helperText={
                      touched.package_duration && errors.package_duration
                    }
                  />
                </Stack>
              </Grid>

              <Grid item md={1} />

              <Grid item xs={12} sm={5.5} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-order">
                    <FormattedMessage id="Order" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title={"Set the order of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="package-order"
                    placeholder="Order"
                    type="number"
                    {...getFieldProps("order")}
                    error={Boolean(touched.order && errors.order)}
                    helperText={touched.order && errors.order}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-note">
                    <FormattedMessage id="English Note" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title={"Set the english note of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    id="package-note"
                    placeholder="Enter english note"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={4}
                    {...getFieldProps("note")}
                  />
                </Stack>
                {touched.note && errors.note && (
                  <FormHelperText
                    error
                    id="standard-weight-helper-text-note"
                    sx={{ pl: 1.75 }}
                  >
                    {errors.note}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="package-note-trans">
                    <FormattedMessage id="Arabic Note" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "10px" }}>
                      <Tooltip title={"Set the arabic note of the vacancy"}>
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    id="package-note-trans"
                    placeholder="enter note"
                    variant="outlined"
                    fullWidth
                    multiline
                    inputProps={{
                      maxLength: 100,
                    }}
                    minRows={4}
                    {...getFieldProps("note_trans")}
                  />
                </Stack>
                {touched.note_trans && errors.note_trans && (
                  <FormHelperText
                    error
                    id="standard-weight-helper-text-note-trans"
                    sx={{ pl: 1.75 }}
                  >
                    {errors.note_trans.toString()}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="center" alignItems="center">
              <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                <Grid item md={2} />
                <Grid item md={4}>
                  <Button
                    disabled={isSubmitting}
                    fullWidth
                    type="submit"
                    variant="contained"
                  >
                    {isSubmitting ? (
                      <CircularProgress
                        size="1.5rem"
                        style={{ color: "white" }}
                      />
                    ) : (
                      <FormattedMessage
                        id={vacancyPackage ? "Update" : "Submit"}
                      />
                    )}
                  </Button>
                </Grid>
                <Grid item md={4}>
                  <Button
                    fullWidth
                    color="secondary"
                    variant="outlined"
                    onClick={handleClose}
                  >
                    <FormattedMessage id="Cancel" />
                  </Button>
                </Grid>
                <Grid item md={2} />
              </Stack>
            </Grid>
          </DialogActions>
        </Form>
      </FormikProvider>
    </ReusableModal>
  );
};

export default AddEditVacancyPackage;
